/**
 * Lazy, request-based board engine.
 *
 * The eager `buildBoardFromModules` solves EVERY space up front — including
 * subspaces the user never opens. At 10k modules that's the whole cost (layout
 * is O(N²) *per space*; any single focal view holds < 500 nodes, but there are
 * thousands of spaces). This engine instead solves a space only when it's
 * requested, caches the result per space, and projects only `depth` levels
 * below the focal space for the preview overlay. Diving issues a new request.
 *
 * The cache unit is `placeSpace` (one space's direct-child placement) — the
 * same primitive the eager path uses, so output is identical per space. An
 * optional `previous` engine lets an edit reuse every unchanged space's solve
 * verbatim (signature-matched); `invalidate` drops the changed ones so the
 * next request re-solves only those.
 */
import type { DependencyEdge, Module } from "../arch-model/model.js";
import { extractWidgetSpec } from "../arch-model/widget-spec.js";
import { moduleKey } from "../arch-model/utils.js";
import { resolveNodeSize } from "./widgetSize.js";
import {
  type LayoutContext,
  type ModuleWithParent,
  type PreviousLayout,
  placeSpace,
  prepareLayout,
} from "./layout.js";
import type { BoardItem, BoardSpace, EdgeWidgetStyle, PlacementOverrides } from "./types.js";
import type { Rect as StressRect } from "./stressLayout.js";
import type { FlowDirection } from "./direction.js";
import type { SpaceSource } from "./viewport.js";

/** Compact signature of an edge/port's resolved interface widget config, so a
 *  `widget.*` edit that changes only appearance (colour, dash, TEMPLATE, or any
 *  non-style prop) still busts the per-space solve cache instead of rendering
 *  stale. Covers the full BoardEdge overlay set — not just the stroke style. */
export function styleSig(s: {
  widgetStyle?: EdgeWidgetStyle;
  widgetTag?: string;
  widgetTemplate?: string;
  widgetProps?: ReadonlyMap<string, unknown>;
}): string {
  const { widgetStyle: ws, widgetTag, widgetTemplate, widgetProps } = s;
  if (!ws && !widgetTag && !widgetTemplate && !widgetProps) return "";
  const props =
    widgetProps && widgetProps.size > 0
      ? [...widgetProps.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(",")
      : "";
  return `~${ws?.color ?? ""}:${ws?.dashed ? "d" : ""}:${ws?.width ?? ""}:${ws?.opacity ?? ""}:${widgetTag ?? ""}:${widgetTemplate ?? ""}:${props}`;
}

/** A request names a space by path and how many levels below it to solve. */
export interface SpaceRequest {
  /** Module-id path from root to the focal space. `[]` = root space. */
  readonly path: readonly string[];
  /** Levels below the focal space to solve for the preview (default 1). */
  readonly depth?: number;
}

export interface BoardEngine {
  /**
   * Solve + cache the requested space, projected `depth` levels deep. Items
   * below `depth` carry `hasSpace: true` with `space` undefined — solved on a
   * later, deeper request (warm from cache).
   */
  getSpace(req: SpaceRequest): BoardSpace;
  /** Drop cached spaces whose inputs changed; next request re-solves them. */
  invalidate(spaceIds: Iterable<string>): void;
  /** Space ids currently solved + cached (for diagnostics / metrics). */
  cachedSpaceIds(): string[];
  /**
   * Merged item rects across every currently-solved space, keyed by item id —
   * a warm-start snapshot. Seeds the eager drag/animation path so it reproduces
   * the engine's layout for the spaces the user has actually seen (never-solved
   * spaces are absent and cold-solve, but they're off-screen). Mirror of
   * `snapshotLayout` over the lazily-solved set.
   */
  snapshot(): Map<string, { x: number; y: number; w: number; h: number }>;
}

interface CacheEntry {
  /** Input signature; a mismatch on rebuild forces a re-solve. */
  readonly sig: string;
  /** Shallow placement (no nested `.space` filled). */
  readonly shallow: BoardSpace;
}

export interface CreateEngineOptions {
  readonly placements?: PlacementOverrides;
  readonly previousLayout?: PreviousLayout;
  /** Prior engine to reuse unchanged per-space solves from (post-edit rebuild). */
  readonly previous?: BoardEngine;
  /**
   * Precomputed `prepareLayout` output. The caller often already runs
   * `prepareLayout` (e.g. for a global connection index) and can reuse it
   * incrementally across edits — passing it here avoids a second O(N+E) pass.
   * Must be built from the SAME `modules`/`edges`/placements.
   */
  readonly prepared?: { ctx: LayoutContext; roots: readonly ModuleWithParent[] };
  /** Screen direction of the dependency flow (default "LR"). Ignored when
   *  `prepared` is supplied — the prepared context carries its own. */
  readonly direction?: FlowDirection;
}

/** Internal: the cache is reachable on a real engine for `previous` reuse. */
interface EngineInternal extends BoardEngine {
  readonly _cache: Map<string, CacheEntry>;
}

const SPACE_SUFFIX = "::space";

/** Map a focal path to its space id. `[]` → root, else `<lastId>::space`. */
function spaceIdForPath(path: readonly string[]): string {
  if (path.length === 0) return `root${SPACE_SUFFIX}`;
  return `${path[path.length - 1]}${SPACE_SUFFIX}`;
}

export function createBoardEngine(
  modules: readonly Module[],
  edges: readonly DependencyEdge[],
  opts: CreateEngineOptions = {},
): BoardEngine {
  const { ctx, roots } =
    opts.prepared ??
    prepareLayout(
      modules,
      edges,
      opts.placements ?? new Map(),
      opts.previousLayout ?? new Map(),
      false,
      undefined,
      undefined,
      undefined,
      opts.direction,
    );

  const cache = new Map<string, CacheEntry>();
  const prevCache = (opts.previous as EngineInternal | undefined)?._cache;
  // Spaces an explicit `invalidate` marked stale — never carried from
  // `previous` even if their signature looks unchanged (covers changes the
  // signature can't see on its own, e.g. cross-level port shifts a caller
  // already computed via `diffModel`).
  const blocked = new Set<string>();

  // Layout/render-affecting content of a module — folded into the space
  // signature so a body edit (same size) still busts the cache.
  const contentSig = (m: Module): string =>
    JSON.stringify([m.name, m.typeName, m.baseType, m.description ?? null, m.fields, m.aspects, m.interfaces, m.surfaces]);

  // Children of a space id. Root holds parentless modules; any other space id
  // is `<moduleId>::space`, holding that module's direct children.
  const childrenOfSpace = (spaceId: string): readonly ModuleWithParent[] => {
    if (spaceId === `root${SPACE_SUFFIX}`) return roots;
    const ownerId = spaceId.slice(0, -SPACE_SUFFIX.length);
    return ctx.byParent.get(ownerId) ?? [];
  };

  // Signature: every input `placeSpace` reads for this space PLUS each child's
  // rendered content (the placed item embeds `meta`/`label`, so a body edit
  // that doesn't change size must still bust the cache). If it's unchanged, the
  // prior solve is byte-identical AND its embedded module data is current.
  const signSpace = (spaceId: string, children: readonly ModuleWithParent[]): string => {
    const spaceParentId =
      spaceId === `root${SPACE_SUFFIX}` ? undefined : spaceId.slice(0, -SPACE_SUFFIX.length);
    const parts: string[] = [];
    for (const m of children) {
      const { w, h } = resolveNodeSize(extractWidgetSpec(m), m);
      const k = moduleKey(m);
      parts.push(`${k}:${w}x${h}:${contentSig(m)}`);
      const pin = (ctx.placements as PlacementOverrides).get(k);
      if (pin) parts.push(`@${pin.x},${pin.y}`);
      const prev = ctx.previousLayout.get(k) as StressRect | undefined;
      if (prev) parts.push(`~${prev.x},${prev.y}`);
      // Cross-level state placeSpace embeds into the items: external ports
      // (solver input + wall badge) and internal-call badges. Both change
      // with edges OUTSIDE this space — a cross-level edge A.x → B parks
      // its aggregated edge at the LCA but its port on x — so without
      // these terms the space would be carried verbatim and the new port
      // would never render (nor reach the edit-gesture port detection).
      // The DETAIL COUNT is load-bearing: hiding one of a peer's members
      // (e.g. `hide Fraud` drops one of Orders' two calls into the Risk
      // space) leaves the port's direction+peer identical but its count
      // 2→1 — so without `xN` the space carries a stale port badge and a
      // view deactivation never restores the count (matches the boundary
      // stub term below, which already keys on `p.details.length`).
      const xp = ctx.externalPorts.get(k);
      if (xp && xp.length > 0) {
        parts.push(
          `p:${xp.map((p) => `${p.direction}${p.peerId ?? ""}x${p.details.length}${styleSig(p)}`).sort().join(",")}`,
        );
      }
      // View boundary stubs render on the item's wall exactly like ports —
      // a stub change (peer/count) must bust this space's cache the same way.
      const bp = ctx.boundaryPorts?.get(k);
      if (bp && bp.length > 0) {
        parts.push(
          `bp:${bp
            .map((p) => `${p.direction}${p.peerId}x${p.details.length}${styleSig(p)}`)
            .sort()
            .join(",")}`,
        );
      }
      const ic = ctx.internalCalls.get(k);
      if (ic && ic.length > 0) parts.push(`i:${JSON.stringify(ic)}`);
    }
    const spaceEdges = ctx.edgesByParent.get(spaceParentId) ?? [];
    // Include the resolved style: a widget.* edit that changes an edge's colour
    // but not its topology must still bust the cache (else it renders stale).
    for (const e of spaceEdges) parts.push(`${e.from}>${e.to}${styleSig(e)}`);
    // `group by` frame membership (root space): a membership flip changes
    // the forced partition — and the rendered frames — while every other
    // input stays identical, so it must bust the cache like any input.
    if (spaceParentId === undefined && ctx.groupFrames?.length) {
      for (const f of ctx.groupFrames) {
        parts.push(`gf:${f.id}<${f.parentId ?? ""}:${[...f.memberBoardIds].sort().join(",")}`);
      }
    }
    return parts.join("|");
  };

  /** Shallow placement for a space, memoised; reused from `previous` when the
   *  signature matches. */
  const shallowFor = (spaceId: string): BoardSpace => {
    const children = childrenOfSpace(spaceId);
    const sig = signSpace(spaceId, children);
    const hit = cache.get(spaceId);
    if (hit && hit.sig === sig) return hit.shallow;
    if (!blocked.has(spaceId)) {
      const carried = prevCache?.get(spaceId);
      if (carried && carried.sig === sig) {
        cache.set(spaceId, carried);
        return carried.shallow;
      }
    }
    // Prior build of this space (own stale entry or the previous engine's) —
    // the frames fallback for no-solve frames (see placeSpace prevSpace).
    const prevShallow = hit?.shallow ?? prevCache?.get(spaceId)?.shallow;
    const shallow = placeSpace(spaceId, children, ctx, prevShallow);
    cache.set(spaceId, { sig, shallow });
    return shallow;
  };

  /** Project a shallow space `depth` levels deep, filling nested `.space`. */
  const project = (spaceId: string, depth: number): BoardSpace => {
    const shallow = shallowFor(spaceId);
    if (depth <= 0) return shallow; // items keep hasSpace, space undefined
    let mutated = false;
    const items: BoardItem[] = shallow.items.map((it) => {
      if (!it.hasSpace) return it;
      mutated = true;
      return { ...it, space: project(`${it.id}${SPACE_SUFFIX}`, depth - 1) };
    });
    return mutated ? { ...shallow, items } : shallow;
  };

  const engine: EngineInternal = {
    _cache: cache,
    getSpace(req) {
      return project(spaceIdForPath(req.path), req.depth ?? 1);
    },
    invalidate(spaceIds) {
      for (const id of spaceIds) {
        cache.delete(id);
        blocked.add(id);
      }
    },
    cachedSpaceIds() {
      return [...cache.keys()];
    },
    snapshot() {
      const out = new Map<string, { x: number; y: number; w: number; h: number }>();
      for (const { shallow } of cache.values()) {
        for (const it of shallow.items) out.set(it.id, { x: it.x, y: it.y, w: it.w, h: it.h });
      }
      return out;
    },
  };
  return engine;
}

/**
 * Adapt a `BoardEngine` into a viewport `SpaceSource` (see viewport.ts). The
 * viewport addresses spaces by *focal path* — a list of SPACE ids
 * (`["root::space", "m5::space", …]`) — whereas the engine takes a MODULE-id
 * path. This bridges the two: strip the `::space` suffix and drop the root
 * segment to get the module path, then `getSpace` it to `depth`.
 *
 * `depth` is how many nested preview levels below the focal to solve (≥1 so the
 * focal's children carry their `.space`, which the dive-in math needs). The
 * caller can override per-request (e.g. export passes Infinity for a full
 * focal subtree).
 */
export function engineSource(engine: BoardEngine, depth = 2): SpaceSource {
  const toModulePath = (focalPath: readonly string[]): string[] =>
    focalPath.slice(1).map((id) => (id.endsWith(SPACE_SUFFIX) ? id.slice(0, -SPACE_SUFFIX.length) : id));
  return {
    at(focalPath, d) {
      return engine.getSpace({ path: toModulePath(focalPath), depth: d ?? depth });
    },
  };
}
